import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AppShell } from './components/shell/AppShell.jsx';
import { Spinner, ToastHost } from './components/common/index.jsx';
import { AnnotationPage } from './pages/AnnotationPage.jsx';
import { GuidelinesPage } from './pages/GuidelinesPage.jsx';
import { ExamplesPage } from './pages/ExamplesPage.jsx';
import { ChangePasswordPage, LoginPage } from './pages/LoginPage.jsx';
import { QueuePage } from './pages/QueuePage.jsx';
import { AgentOutputsPage } from './pages/admin/AgentOutputsPage.jsx';
import { AssignmentsPage } from './pages/admin/AssignmentsPage.jsx';
import { DashboardPage } from './pages/admin/DashboardPage.jsx';
import { SamplesPage } from './pages/admin/SamplesPage.jsx';
import { UsersPage } from './pages/admin/UsersPage.jsx';
import { useAuthStore } from './store/authStore.js';

export default function App() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const init = useAuthStore((s) => s.init);

  useEffect(() => {
    init();
  }, [init]);

  if (status === 'loading') return <Spinner label="Signing in…" />;
  if (status === 'anonymous') {
    return (
      <>
        <LoginPage />
        <ToastHost />
      </>
    );
  }

  // Everything else is gated until a generated password has been replaced.
  if (user.must_change_password) {
    return (
      <>
        <ChangePasswordPage />
        <ToastHost />
      </>
    );
  }

  return (
    <>
      <AppShell>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/queue" element={<QueuePage />} />
          <Route path="/annotate/:taskId" element={<AnnotationPage />} />
          <Route path="/guidelines" element={<GuidelinesPage />} />
          <Route path="/examples" element={<ExamplesPage />} />

          <Route path="/admin" element={<RequireAdmin><DashboardPage /></RequireAdmin>} />
          <Route path="/admin/samples" element={<RequireAdmin><SamplesPage /></RequireAdmin>} />
          <Route path="/admin/agent-outputs" element={<RequireAdmin><AgentOutputsPage /></RequireAdmin>} />
          <Route path="/admin/assignments" element={<RequireAdmin><AssignmentsPage /></RequireAdmin>} />
          <Route path="/admin/users" element={<RequireAdmin><UsersPage /></RequireAdmin>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
      <ToastHost />
    </>
  );
}

/**
 * First-time annotators land on the guidelines rather than an empty queue; everyone
 * else goes to their work.
 */
function HomeRedirect() {
  const user = useAuthStore((s) => s.user);
  const bootstrap = useAuthStore((s) => s.bootstrap);
  const currentVersion = bootstrap?.guidelines?.version;

  if (currentVersion && (user.guidelines_ack_version ?? 0) < currentVersion) {
    return <GuidelinesPage requireAck />;
  }
  return <QueuePage />;
}

function RequireAdmin({ children }) {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace state={{ from: location }} />;
  }
  return children;
}
